import { Card } from 'components/ui/card';
import { useIntl } from 'react-intl';
import { GlobalSearchDidYouMean } from 'types/globalSearch';
import { DidYouMean } from './ResultCard';

export interface NothingFoundProps {
    query: string;
    didYouMean: GlobalSearchDidYouMean | null;
    onDidYouMean: (term: string) => void;
}

/** The three ways a query that found nothing is most often rescued. */
const TIP_IDS = ['global-search.tip.drop-a-term', 'global-search.tip.identifier', 'global-search.tip.local-spelling'];

/**
 * What the results page shows when the query matched nothing.
 *
 * It never hints that a record exists but is out of reach: search only ever counts what the reader
 * may open, so a placeholder row or a "3 hidden" count here would be both a leak and a lie. The
 * correction is the one thing on it that can actually be acted on, so it is offered with the number
 * of results the better spelling returns rather than as a bare "did you mean".
 */
export function NothingFound({ query, didYouMean, onDidYouMean }: NothingFoundProps) {
    const intl = useIntl();

    return (
        <Card className="w-full max-w-[720px] gap-0 rounded-[20px] px-7 py-6">
            <h1 className="text-h4 text-grey-700 m-0 font-semibold">
                {intl.formatMessage({ id: 'global-search.no-results-for' }, { query })}
            </h1>
            {didYouMean !== null && (
                <p className="border-l-primary bg-primary-surface-subtle text-grey-700 mt-4 mb-0 border-l-[3px] px-4 py-3 text-sm leading-[22px]">
                    <DidYouMean term={didYouMean.term} onSelect={onDidYouMean} />{' '}
                    {intl.formatMessage({ id: 'global-search.did-you-mean-count' }, { count: didYouMean.resultCount })}
                </p>
            )}
            <div className="border-divider mt-5 border-t pt-4">
                <div className="text-grey-700 text-xs leading-[18px] font-semibold">
                    {intl.formatMessage({ id: 'global-search.other-things-to-try' })}
                </div>
                <ul className="text-grey-600 mt-2 mb-0 flex list-disc flex-col gap-1 pl-5 text-sm leading-[22px]">
                    {TIP_IDS.map((id) => (
                        <li key={id}>{intl.formatMessage({ id })}</li>
                    ))}
                </ul>
            </div>
            <p className="text-grey-500 mt-5 mb-0 text-xs leading-[18px]">
                {intl.formatMessage({ id: 'global-search.coverage-note' })}
            </p>
        </Card>
    );
}
